// CalendarPage.jsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import EventTile from './EventTile'; 
import EventDetailsModal from './EventDetailsModal';
import DayColumn from './DayColumn';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Firebase } from '../../../lib/firebase'; // Ensure correct import path

const CalendarPage = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // MAIN FETCH LOGIC (Server-Side Proxy)
    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const functions = getFunctions(Firebase);
                // Call the Python Cloud Function we created
                const getCalendarEvents = httpsCallable(functions, 'get_calendar_events');
                
                console.log("Calling get_calendar_events Cloud Function...");
                const result = await getCalendarEvents();
                const data = result.data;
                
                console.log("Backend Debug:", data.debug_message);
                console.log("Calendars Found:", data.debug_calendars);

                if (data && data.events) {
                    console.log(`Received ${data.events.length} events from backend.`);
                    // Events are already formatted by the backend, but we need to ensure Dates are Date objects
                    const processedEvents = data.events.map(evt => ({
                        ...evt,
                        // Parse ISO strings back to Date objects
                        start: {
                            ...evt.start,
                            dateTime: evt.start.dateTime, 
                            date: evt.start.date
                        },
                        end: {
                            ...evt.end,
                            dateTime: evt.end.dateTime,
                            date: evt.end.date
                        }
                    }));

                    // Sort by start time (backend usually does this, but safety first)
                    processedEvents.sort((a, b) => {
                        const startA = new Date(a.start.dateTime || a.start.date);
                        const startB = new Date(b.start.dateTime || b.start.date);
                        return startA - startB;
                    });

                    setEvents(processedEvents);
                } else {
                    console.warn("Backend returned no events array:", data);
                    setEvents([]);
                }

            } catch (err) {
                console.error("Error fetching calendar events:", err);
                setError(err.message || "Failed to load calendar events.");
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
        
        // Optional: Refresh every 15 minutes
        const interval = setInterval(fetchEvents, 15 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);


    // --- DATA PROCESSING FOR WEEK VIEW ---
    const getWeekDays = () => {
        const days = [];
        const today = new Date();
        // Start from today
        for (let i = 0; i < 7; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            days.push(d);
        }
        return days;
    };

    const weekDays = getWeekDays();

    // Group events by day string (YYYY-MM-DD) for easy lookup
    const eventsByDay = events.reduce((acc, event) => {
        const start = event.start.dateTime ? new Date(event.start.dateTime) : new Date(event.start.date);
        const dateKey = start.toLocaleDateString(); // Local date string as key
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(event);
        return acc;
    }, {});


    // --- MODAL STATE ---
    const [selectedEvent, setSelectedEvent] = useState(null);

    // --- STYLES ---
    const containerStyle = {
        height: '100vh',
        width: '100%', 
        paddingTop: '20px',
        paddingBottom: '20px',
        color: '#ffffff', // White text for color background
        backgroundColor: '#d53f8c', // Pink Theme matching Command Center Tile
        display: 'flex',
        flexDirection: 'column',
    };

    if (error) {
        return (
            <Box style={{...containerStyle, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fefcbf', color: '#744210'}}>
                <Typography variant="h4" gutterBottom>Calendar Error</Typography>
                <Typography variant="body1" sx={{ maxWidth: '600px', textAlign: 'center' }}>
                    {error}
                </Typography>
                <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
                     Please verify your calendar sharing settings in the Setup page.
                </Typography>
            </Box>
        );
    }

    return (
        <Box style={containerStyle}>
            {/* Header */}
            <Box px={4} mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton 
                        onClick={() => navigate('/family-central')} 
                        sx={{ 
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography 
                        variant="h2" 
                        fontWeight="900" 
                        sx={{ color: 'white', letterSpacing: '-0.02em' }}
                    >
                        Family Calendar
                    </Typography>
                </Box>
            </Box>

            {loading && <Box display="flex" justifyContent="center" m={4}><CircularProgress color="secondary" /></Box>}
            
            {!loading && (
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    gap: 2, 
                    px: 3,
                    pb: 2,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    // Hide Horizontal Scrollbar
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}>
                    {weekDays.map((day, index) => {
                        const dateKey = day.toLocaleDateString();
                        const dailyEvents = eventsByDay[dateKey] || [];
                        const isToday = index === 0;

                        return (
                            <DayColumn 
                                key={dateKey}
                                day={day}
                                events={dailyEvents}
                                isToday={isToday}
                                onEventClick={setSelectedEvent}
                            />
                        );
                    })}
                </Box>
            )}

            <EventDetailsModal 
                open={!!selectedEvent} 
                onClose={() => setSelectedEvent(null)} 
                event={selectedEvent} 
            />
        </Box>
    );
};

export default CalendarPage;
